/**
 * ResetPassword Page — Industry design system ("Recuperar Senha" artboard,
 * reset stage).
 *
 * Single centered 460px blueprint card on the light ground: Z plate +
 * wordmark, "Definir nova senha" eyebrow/title, two grammar password
 * fields and the artboard's rule checklist (square 15px check plates —
 * accent-filled when the rule is met, hairline/muted otherwise) in place
 * of the old traffic-light strength bar. The done stage renders the
 * accent-tint note + "Ir para o login".
 *
 * Backend contract unchanged: `authService.resetPassword(token, password)`
 * posts to `/auth/password-reset/confirm/` (signed 24h one-time token).
 */

import { useState, useEffect, useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../services/auth';

const MIN_PASSWORD_LENGTH = 8;
const REDIRECT_DELAY_MS = 3200;

const HEADING = 'var(--font-heading)';
const INK = 'var(--color-neutral-900)';
const ACC = 'var(--color-accent)';
const ACC7 = 'var(--color-accent-700)';
const ACC8 = 'var(--color-accent-800)';
const MUTED58 = 'color-mix(in srgb, var(--color-text) 58%, transparent)';
const MUTED72 = 'color-mix(in srgb, var(--color-text) 72%, transparent)';

const labelSx = {
  display: 'block',
  fontFamily: HEADING,
  fontSize: '9.5px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: MUTED58,
  mb: '4px',
};

const inputStyle = {
  width: '100%',
  minHeight: 40,
  fontSize: 14.5,
  background: 'transparent',
  border: '1px solid var(--color-divider)',
  borderRadius: 0,
  padding: '0 11px',
  paddingRight: 44,
  boxSizing: 'border-box',
  fontFamily: 'var(--font-body)',
  color: 'var(--color-text)',
};

const primaryBtnSx = {
  width: '100%',
  minHeight: 44,
  mt: '20px',
  border: `1px solid ${ACC}`,
  background: ACC,
  color: 'var(--color-bg)',
  fontFamily: HEADING,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  borderRadius: 0,
  cursor: 'pointer',
  '&:hover': { background: 'var(--color-accent-600)', borderColor: 'var(--color-accent-600)' },
  '&:active': { background: ACC7 },
  '&:disabled': { opacity: 0.45, cursor: 'default' },
};

const eyeSx = {
  position: 'absolute',
  right: 4,
  top: '50%',
  transform: 'translateY(-50%)',
  color: MUTED58,
  borderRadius: 0,
};

/**
 * Grammar rule checklist replacing the old 0..4 colored strength scale.
 * Each rule renders a 15px square plate: accent fill + check when met,
 * hairline border + muted otherwise (per the "Recuperar Senha" artboard).
 */
function buildPasswordRules(password, confirm) {
  return [
    { t: `Mínimo de ${MIN_PASSWORD_LENGTH} caracteres`, ok: password.length >= MIN_PASSWORD_LENGTH },
    { t: 'Letra maiúscula e minúscula', ok: /[a-z]/.test(password) && /[A-Z]/.test(password) },
    { t: 'Número ou símbolo', ok: /[0-9\W]/.test(password) },
    { t: 'Confirmação igual', ok: Boolean(password) && password === confirm },
  ];
}

function RuleChecklist({ rules }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {rules.map((r) => (
        <Box key={r.t} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              width: 15,
              height: 15,
              fontFamily: HEADING,
              fontSize: 9,
              fontWeight: 600,
              border: `1px solid ${r.ok ? ACC : 'var(--color-divider)'}`,
              background: r.ok ? ACC : 'transparent',
              color: r.ok ? 'var(--color-bg)' : MUTED58,
            }}
          >
            {r.ok ? '✓' : ''}
          </Box>
          <Box
            component="span"
            sx={{
              fontFamily: HEADING,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: r.ok ? ACC7 : MUTED58,
            }}
          >
            {r.t}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    passwordConfirm: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Link de recuperação inválido ou ausente. Solicite um novo link pela tela "Esqueceu a senha?".');
    }
  }, [token]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => navigate('/login'), REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  const rules = useMemo(
    () => buildPasswordRules(formData.password, formData.passwordConfirm),
    [formData.password, formData.passwordConfirm]
  );

  const canSubmit =
    !!token
    && !loading
    && !success
    && formData.password.length >= MIN_PASSWORD_LENGTH
    && formData.password === formData.passwordConfirm;

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;

    if (formData.password.length < MIN_PASSWORD_LENGTH) {
      setError(`A senha deve ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`);
      return;
    }
    if (formData.password !== formData.passwordConfirm) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await authService.resetPassword(token, formData.password);
      setSuccess(true);
    } catch (err) {
      setError(
        err.response?.data?.error
          || err.response?.data?.detail
          || 'Não foi possível redefinir a senha. O link pode ter expirado — solicite um novo.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className="zb-industry"
      sx={{
        minHeight: '100dvh',
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
        fontFamily: 'var(--font-body)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: '40px 24px',
        boxSizing: 'border-box',
      }}
    >
      <Box
        component="a"
        href="/login"
        sx={{ display: 'flex', alignItems: 'center', gap: '9px', textDecoration: 'none', color: 'var(--color-text)' }}
      >
        <Box component="span" sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, border: '1.5px solid var(--color-text)', fontFamily: HEADING, fontSize: 15, fontWeight: 600 }}>
          Z
        </Box>
        <Box component="span" sx={{ fontFamily: HEADING, fontSize: 19, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Zulbot
        </Box>
      </Box>

      <Box
        className="blueprint"
        sx={{ width: '100%', maxWidth: 460, mt: 3, p: '26px', background: 'transparent', boxShadow: 'var(--ind-shadow-md)' }}
      >
        <i className="corner tl" /><i className="corner tr" /><i className="corner bl" /><i className="corner br" />

        <Box sx={{ fontFamily: HEADING, fontSize: 10, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: ACC7 }}>
          {success ? 'Tudo pronto' : 'Definir nova senha'}
        </Box>
        <Typography
          component="h1"
          sx={{ m: '8px 0 0', fontFamily: HEADING, fontSize: 26, fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase' }}
        >
          {success ? 'Senha redefinida!' : 'Definir nova senha'}
        </Typography>
        <Typography sx={{ m: '7px 0 0', fontSize: 13.5, lineHeight: 1.55, color: MUTED72 }}>
          {success
            ? 'Sua nova senha já está ativa. Vamos levar você para a tela de login em instantes.'
            : `Escolha uma senha forte com pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`}
        </Typography>

        {!success ? (
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', mt: '20px' }}>
              <Box>
                <Box component="label" htmlFor="reset-password" sx={labelSx}>
                  Nova senha
                </Box>
                <Box sx={{ position: 'relative' }}>
                  <input
                    id="reset-password"
                    className="ind-input"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    disabled={!token}
                    autoComplete="new-password"
                    placeholder={`mínimo ${MIN_PASSWORD_LENGTH} caracteres`}
                    style={inputStyle}
                  />
                  <IconButton
                    onClick={() => setShowPassword((s) => !s)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    size="small"
                    sx={eyeSx}
                  >
                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>

              <Box>
                <Box component="label" htmlFor="reset-password-confirm" sx={labelSx}>
                  Confirmar nova senha
                </Box>
                <Box sx={{ position: 'relative' }}>
                  <input
                    id="reset-password-confirm"
                    className="ind-input"
                    name="passwordConfirm"
                    type={showConfirm ? 'text' : 'password'}
                    value={formData.passwordConfirm}
                    onChange={handleChange}
                    required
                    disabled={!token}
                    autoComplete="new-password"
                    placeholder="repita a senha"
                    style={inputStyle}
                  />
                  <IconButton
                    onClick={() => setShowConfirm((s) => !s)}
                    tabIndex={-1}
                    aria-label={showConfirm ? 'Ocultar senha' : 'Mostrar senha'}
                    size="small"
                    sx={eyeSx}
                  >
                    {showConfirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>

              <RuleChecklist rules={rules} />
            </Box>

            {error && (
              <Box sx={{ mt: '14px', p: '9px 11px', border: `1px solid ${INK}`, fontSize: 12.5, lineHeight: 1.45, color: INK }}>
                {error}
              </Box>
            )}

            <Box component="button" type="submit" disabled={!canSubmit} sx={primaryBtnSx}>
              {loading ? 'Salvando...' : 'Redefinir senha'}
            </Box>
          </form>
        ) : (
          <Box>
            <Box
              sx={{
                mt: '20px',
                p: '12px 13px',
                border: `1px solid ${ACC}`,
                background: 'color-mix(in srgb, var(--color-accent) 7%, transparent)',
                fontSize: 13,
                lineHeight: 1.55,
                color: ACC8,
              }}
            >
              Senha redefinida com sucesso. Se você não reconhece esta alteração, fale com o administrador da conta imediatamente.
            </Box>
            <Box component="button" type="button" onClick={() => navigate('/login')} sx={primaryBtnSx}>
              Ir para o login agora
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            mt: '18px',
            pt: '15px',
            borderTop: '1px dashed var(--color-neutral-300)',
            flexWrap: 'wrap',
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => navigate('/login')}
            sx={{
              border: 0,
              background: 'transparent',
              p: 0,
              cursor: 'pointer',
              fontFamily: HEADING,
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: ACC7,
              '&:hover': { color: ACC8 },
            }}
          >
            Voltar para o login
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box component="span" sx={{ fontFamily: HEADING, fontSize: '9.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'color-mix(in srgb, var(--color-text) 55%, transparent)' }}>
            Link expira em 24h
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPassword;
